import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { X } from "lucide-react";

export interface JsonEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  configData: string;
  setConfigData: (val: string) => void;
  onSave: (saveAsNewVersion: boolean, versionName?: string) => void;
  readOnly?: boolean;
  allowVersioning?: boolean;
}

export const JsonEditorModal = ({
  isOpen,
  onClose,
  title,
  configData,
  setConfigData,
  onSave,
  readOnly = false,
  allowVersioning = false,
}: JsonEditorModalProps) => {
  const [versionName, setVersionName] = React.useState("");

  React.useEffect(() => {
    if (isOpen) {
      setVersionName("");
    }
  }, [isOpen]);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100000000]" onClose={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-25" />

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {readOnly ? "View Configuration" : "Edit Configuration"} - {title}
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="px-6 py-4">
                  <textarea
                    className="w-full h-96 p-4 text-sm font-mono border rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 outline-none resize-none disabled:bg-gray-50 disabled:text-gray-600"
                    value={configData}
                    onChange={(e) => setConfigData(e.target.value)}
                    disabled={readOnly}
                    readOnly={readOnly}
                  />
                </div>
                <div className="bg-gray-50 px-6 py-4 flex flex-row-reverse rounded-b-xl border-t border-gray-200 gap-2">
                  {!readOnly && (
                    <>
                      {allowVersioning ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Version description..."
                            className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm h-[38px] px-3 border"
                            value={versionName}
                            onChange={(e) => setVersionName(e.target.value)}
                          />
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none"
                            onClick={() => {
                              onSave(true, versionName.trim() || "Edited via JSON Editor");
                              onClose();
                            }}
                          >
                            Save as New Version
                          </button>
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none"
                            onClick={() => {
                              onSave(false, versionName.trim() || undefined);
                              onClose();
                            }}
                          >
                            Overwrite
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none"
                          onClick={() => {
                            onSave(false);
                            onClose();
                          }}
                        >
                          Save Changes
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
